/**
 * Article API service
 * Publishing engine fetch operations for public and admin
 * @module features/articles/articleService
 */

import api from '../../app/axios';
import {
  fetchArticleBySlug,
  fetchArticlesList,
} from '../../api/localizedContentApi';
import { normalizeCmsEntity } from '../../utils/normalizeCmsResponse';

/**
 * Fetch a public article by language and slug
 */
const getPublicArticle = async (lang, slug) => {
  const response = await fetchArticleBySlug({ locale: lang, slug });
  return {
    article: normalizeCmsEntity(response.data, lang),
    locale: response.locale,
    market: response.market,
    fallbackUsed: response.fallbackUsed,
    translationStatus: response.translationStatus,
    isIndexable: response.isIndexable,
  };
};

/**
 * List public articles with filters
 */
const listPublicArticles = async (params) => {
  const response = await fetchArticlesList({
    locale: params?.locale || params?.lang || 'en',
    page: params?.page,
    limit: params?.limit,
    search: params?.search,
    category: params?.category,
    tag: params?.tag,
  });

  return {
    articles: response.items.map((item) => normalizeCmsEntity(item, params?.locale || params?.lang || 'en')),
    pagination: response.pagination,
    locale: response.locale,
    market: response.market,
  };
};

/**
 * Get all articles — admin (includes draft/scheduled)
 */
const getAllArticles = async (params) => {
  const response = await api.get('/articles/admin/all', { params });
  return response.data;
};

/**
 * Get a single article by ID — admin
 */
const getArticleById = async (articleId) => {
  const response = await api.get(`/articles/admin/${articleId}`);
  return response.data;
};

/**
 * Create a new article — admin
 */
const createArticle = async (articleData) => {
  const response = await api.post('/articles', articleData);
  return response.data;
};

/**
 * Update an article — admin
 */
const updateArticle = async (articleId, articleData) => {
  const response = await api.put(`/articles/${articleId}`, articleData);
  return response.data;
};

/**
 * Publish or unpublish an article — admin
 */
const publishArticle = async (articleId, action) => {
  const response = await api.patch(`/articles/publish/${articleId}`, { action });
  return response.data;
};

/**
 * Schedule an article — admin
 */
const scheduleArticle = async (articleId, scheduledAt) => {
  const response = await api.patch(`/articles/schedule/${articleId}`, { scheduledAt });
  return response.data;
};

/**
 * Generate an excerpt draft for a locale — admin
 */
const generateExcerptDraft = async (articleId, locale, force = true) => {
  const response = await api.post(`/articles/${articleId}/actions/excerpt-draft`, { locale, force });
  return response.data;
};

/**
 * Generate an SEO draft for a locale — admin
 */
const generateSeoDraft = async (articleId, locale, force = true) => {
  const response = await api.post(`/articles/${articleId}/actions/seo-draft`, { locale, force });
  return response.data;
};

/**
 * Duplicate content from one locale to another — admin
 */
const duplicateArticleLanguage = async (articleId, sourceLocale, targetLocale) => {
  const response = await api.post(`/articles/${articleId}/actions/duplicate-language`, {
    sourceLocale,
    targetLocale,
  });
  return response.data;
};

/**
 * Delete an article — admin
 */
const deleteArticle = async (articleId) => {
  const response = await api.delete(`/articles/${articleId}`);
  return response.data;
};

/**
 * Get article analytics — admin
 */
const getArticleAnalytics = async () => {
  const response = await api.get('/articles/admin/analytics');
  return response.data;
};

const articleService = {
  getPublicArticle,
  listPublicArticles,
  getAllArticles,
  getArticleById,
  createArticle,
  updateArticle,
  publishArticle,
  scheduleArticle,
  generateExcerptDraft,
  generateSeoDraft,
  duplicateArticleLanguage,
  deleteArticle,
  getArticleAnalytics,
};

export default articleService;

// Developed and Designed by Trophy Developers
